import React, { useState, useMemo } from 'react';
import { useApi, forSale as forSaleApi } from '../services/api';
import { ForSaleGrid, ContactOwnerModal } from './ForSaleListings';

const sel = { padding: '0.5rem 0.65rem', minHeight: 40, border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)', background: 'var(--surface)', boxSizing: 'border-box' };

// ── Cross-property "For Sale" browse tab ─────────────────────────────────────
export default function BrowseForSale() {
  const { data, loading, error, refetch } = useApi('/properties/for-sale');
  const listings = useMemo(() => data?.listings || [], [data]);

  const [search, setSearch]         = useState('');
  const [filterType, setFilterType] = useState('');
  const [weekMin, setWeekMin]       = useState('');
  const [weekMax, setWeekMax]       = useState('');
  const [priceMin, setPriceMin]     = useState('');
  const [priceMax, setPriceMax]     = useState('');
  const [sortBy, setSortBy]         = useState('week');
  const [offerTarget, setOfferTarget] = useState(null);

  const roomTypes = useMemo(() => [...new Set(listings.map(l => l.room_type).filter(Boolean))].sort(), [listings]);

  const filtered = useMemo(() => {
    let rows = listings.slice();
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(l =>
        l.property_name?.toLowerCase().includes(q) ||
        l.property_location?.toLowerCase().includes(q) ||
        l.unit_number?.toLowerCase().includes(q)
      );
    }
    if (filterType) rows = rows.filter(l => l.room_type === filterType);
    if (weekMin)    rows = rows.filter(l => l.week_number >= parseInt(weekMin, 10));
    if (weekMax)    rows = rows.filter(l => l.week_number <= parseInt(weekMax, 10));
    if (priceMin)   rows = rows.filter(l => l.asking_price_cents >= parseFloat(priceMin) * 100);
    if (priceMax)   rows = rows.filter(l => l.asking_price_cents <= parseFloat(priceMax) * 100);
    rows.sort((a, b) => {
      if (sortBy === 'price_asc')  return a.asking_price_cents - b.asking_price_cents;
      if (sortBy === 'price_desc') return b.asking_price_cents - a.asking_price_cents;
      return a.week_number - b.week_number; // 'week'
    });
    return rows;
  }, [listings, search, filterType, weekMin, weekMax, priceMin, priceMax, sortBy]);

  const hasFilters = search || filterType || weekMin || weekMax || priceMin || priceMax;
  function clearFilters() {
    setSearch(''); setFilterType(''); setWeekMin(''); setWeekMax(''); setPriceMin(''); setPriceMax('');
  }

  async function handleRemove(listing) {
    if (!window.confirm(`Remove your listing for Unit ${listing.unit_number}?`)) return;
    await forSaleApi.remove(listing.id);
    refetch();
  }

  if (loading) return <p style={{ color: 'var(--ink-muted)' }}>Loading for-sale listings…</p>;
  if (error) return <div className="alert alert-error">Failed to load for-sale listings. Please refresh.</div>;

  return (
    <div>
      {/* Explainer for buyers unfamiliar with timeshare weeks */}
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ink)', lineHeight: 1.6, marginBottom: '1.25rem', maxWidth: 720 }}>
        Buying a timeshare week means purchasing the right to stay in a specific unit during the same week every year,
        directly from the current owner. You pay a one-time purchase price plus the resort's annual maintenance fee going
        forward — no financing through ShareTime and no transaction fee.
      </p>

      <p style={{ color: 'var(--ink-muted)', fontSize: 'var(--text-base)', marginBottom: '1rem' }}>
        {filtered.length} unit{filtered.length !== 1 ? 's' : ''} for sale
      </p>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1.25rem', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search resort, location, or unit…"
          style={{ ...sel, minWidth: 200, flex: '1 1 200px' }}
        />
        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={sel}>
          <option value="">All Room Types</option>
          {roomTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>Week</span>
          <input type="number" min="1" max="53" placeholder="min" value={weekMin} onChange={e => setWeekMin(e.target.value)} style={{ ...sel, width: 70 }} />
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-faint)' }}>–</span>
          <input type="number" min="1" max="53" placeholder="max" value={weekMax} onChange={e => setWeekMax(e.target.value)} style={{ ...sel, width: 70 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-muted)' }}>Price $</span>
          <input type="number" min="0" placeholder="min" value={priceMin} onChange={e => setPriceMin(e.target.value)} style={{ ...sel, width: 85 }} />
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-faint)' }}>–</span>
          <input type="number" min="0" placeholder="max" value={priceMax} onChange={e => setPriceMax(e.target.value)} style={{ ...sel, width: 85 }} />
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={sel}>
          <option value="week">Sort: Week Number</option>
          <option value="price_asc">Sort: Price — Low to High</option>
          <option value="price_desc">Sort: Price — High to Low</option>
        </select>
        {hasFilters && (
          <button onClick={clearFilters} style={{ background: 'none', border: 'none', color: 'var(--ink-muted)', cursor: 'pointer', fontSize: 'var(--text-sm)', textDecoration: 'underline', padding: 0 }}>
            Clear filters
          </button>
        )}
      </div>

      <ForSaleGrid listings={filtered} onOffer={setOfferTarget} onRemove={handleRemove} showProperty />

      {offerTarget && (
        <ContactOwnerModal listing={offerTarget} propertyName={offerTarget.property_name} onClose={() => setOfferTarget(null)} />
      )}
    </div>
  );
}
